import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon, { type IconProps } from "@/v2-ui/icon"
import Tooltip from "@/v2-ui/tooltip"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import { useTableContext } from "./table.context"

const STATUS_FILTER_MARKER = "__statusFilter"

export type StatusFilterValue = {
  [STATUS_FILTER_MARKER]: true
  eq: string
}

export function isStatusFilterValue(
  value: unknown,
): value is StatusFilterValue {
  return (
    typeof value === "object" && value !== null && STATUS_FILTER_MARKER in value
  )
}

type StatusFilterContextValue = {
  value: number | null
  /**
   * True while the search box pauses this group: the selected pill is held,
   * not applied, and shows as such (see Table's `isStatusFilterPaused`).
   */
  isPaused: boolean
  onChange(
    value: number | null,
    filterValue?: string,
    filterColumn?: string,
  ): void
  registerOption(
    value: number | null,
    filterValue: string | undefined,
    filterColumn: string | undefined,
  ): void
  isLoading?: boolean
}

// Public onChange signature. We expose both the raw numeric `value`
// (used by older tables to switch on hard-coded constants) AND the
// option's `filterValue` string (used by tables that drive a logical
// bucket from a typed string union). Consumers pick whichever is
// most convenient -- ignoring the second arg keeps the legacy single-
// argument call sites working unchanged.
export type TableStatusFilterChangeHandler = (
  value: number | null,
  filterValue: string | null,
) => void

const StatusFilterContext = createContext<StatusFilterContextValue | null>(null)

function useStatusFilterContext() {
  const ctx = useContext(StatusFilterContext)
  if (!ctx) {
    throw new Error(
      "Table.StatusFilter.Option must be used inside Table.StatusFilter",
    )
  }
  return ctx
}

export type TableStatusFilterProps = Omit<
  ComponentProps<"div">,
  "onChange" | "defaultValue"
> & {
  /** TanStack column ID to set a filter on when an option is selected. */
  column?: string
  defaultValue?: number | null
  /**
   * Called whenever the active pill changes. Receives both the
   * numeric `value` of the pill and the string `filterValue` (the
   * option's `filterValue` prop, or `String(value)` when omitted).
   * Single-argument call sites keep working because we only added
   * a parameter; tables that bucket on a string union should use
   * the second argument.
   */
  onChange?: TableStatusFilterChangeHandler
  isLoading?: boolean
  /**
   * Name of the URL search parameter used to persist the active pill.
   * The value stored is the option's `filterValue` (or `String(value)`
   * when `filterValue` is omitted). Pass `null` to disable persistence.
   * Defaults to `"status"` so every table that drops a `Table.StatusFilter`
   * gets shareable URLs out of the box; tables that need disambiguation
   * (e.g. nested or side-by-side) can pass a unique name.
   */
  urlParam?: string | null
}

function TableStatusFilter(props: TableStatusFilterProps) {
  const {
    column,
    defaultValue = null,
    onChange,
    isLoading,
    urlParam = "status",
    className,
    children,
    ...divProps
  } = props

  const { table, isStatusFilterPaused, resumeStatusFilter } = useTableContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlValue = urlParam ? searchParams.get(urlParam) : null

  // Registry of (value, filterColumn) for every rendered option keyed by
  // its filter string, so we can map the URL string back to the numeric
  // pill `value` (and the column it filters) once children have mounted.
  // Without this we'd have to enumerate React children -- which doesn't
  // work for dynamic option lists.
  const optionsRef = useRef<
    Map<string, { value: number | null; filterColumn?: string }>
  >(new Map())
  // Every column this group can set a filter on: the group `column` plus
  // any per-option `filterColumn` override. Switching pills clears all of
  // them before setting the new one, so the group stays single-select even
  // when an option (e.g. "Flagged") targets a different column.
  const managedColumnsRef = useRef<Set<string>>(new Set())

  const registerOption = useCallback(
    (
      val: number | null,
      filterValue: string | undefined,
      filterColumn: string | undefined,
    ) => {
      const key = filterValue ?? String(val ?? "")
      optionsRef.current.set(key, { value: val, filterColumn })
      const managed = filterColumn ?? column
      if (managed) managedColumnsRef.current.add(managed)
    },
    [column],
  )

  // The registered filter string and column for a pill value — the key the
  // URL and the server filter speak, resolved the same way whether the value
  // came from the URL, the default or a pause ending.
  const resolveOption = useCallback((val: number) => {
    for (const [key, entry] of optionsRef.current) {
      if (entry.value === val) {
        return { key, filterColumn: entry.filterColumn }
      }
    }
    return { key: String(val), filterColumn: undefined }
  }, [])

  const [value, setValue] = useState<number | null>(defaultValue)
  const didInitRef = useRef(false)
  const lastUrlSyncRef = useRef<string | null>(null)
  // The filter string of the last onChange call, so a pause or resume is
  // only reported when it changes what the consumer applies.
  const lastReportedRef = useRef<string | null | undefined>(undefined)

  // The "All" pill filters nothing, so there is nothing to pause: the group
  // only reads (and reports) as paused while it holds a filter the search
  // bypasses.
  const isPaused = isStatusFilterPaused && value !== null

  const clearStatusFilter = useCallback(() => {
    const managed = managedColumnsRef.current
    table.setColumnFilters((prev) => prev.filter((f) => !managed.has(f.id)))
  }, [table])

  const setStatusFilter = useCallback(
    (eq: string, filterColumn?: string) => {
      const col = filterColumn ?? column
      if (!col) return
      const wrapped: StatusFilterValue = { [STATUS_FILTER_MARKER]: true, eq }
      const managed = managedColumnsRef.current
      table.setColumnFilters((prev) => {
        // Clear every column this group manages so the pills stay
        // mutually exclusive across both the status and flag columns.
        const without = prev.filter((f) => !managed.has(f.id))
        return [...without, { id: col, value: wrapped }]
      })
    },
    [table, column],
  )

  // Initial sync: URL > defaultValue. Runs once after first render so
  // option registrations have populated `optionsRef`. We deliberately
  // schedule via microtask so registerOption calls land first.
  useEffect(() => {
    if (didInitRef.current) return
    didInitRef.current = true

    // Wait one microtask so all child Options have registered.
    queueMicrotask(() => {
      if (urlValue !== null && urlValue !== "") {
        const resolved = optionsRef.current.get(urlValue)
        if (resolved !== undefined) {
          // Mark this value as already-synced so handleChange doesn't
          // re-write the URL with the same value.
          lastUrlSyncRef.current = urlValue
          setValue(resolved.value)
          // Pass the URL string as the second arg so callers that
          // bucket on the filterValue (e.g. UptimeIncidentTable) get
          // the same string they registered under, not just a number.
          onChange?.(resolved.value, urlValue)
          lastReportedRef.current = urlValue
          if (resolved.value !== null) {
            setStatusFilter(urlValue, resolved.filterColumn)
          } else {
            clearStatusFilter()
          }
          return
        }
      }
      if (column && defaultValue !== null) {
        // Resolve defaultValue through the same registry the URL path uses,
        // so an option's `filterValue` override (e.g. a string-bucketed
        // column like "active"/"deleted") is honoured on first paint too —
        // not just the raw numeric value, which only happens to be correct
        // when no option overrides it.
        const resolved = resolveOption(defaultValue)
        setStatusFilter(resolved.key, resolved.filterColumn)
      }
    })
  }, [
    column,
    defaultValue,
    urlValue,
    onChange,
    resolveOption,
    setStatusFilter,
    clearStatusFilter,
  ])

  // A table that filters from the pill's value in its own state (no
  // `column`: the membership boards' onlyDeleted, the layers' isEnabled,
  // the uptime buckets) is out of the table's reach, so it is told what
  // APPLIES rather than what is held: "All" when the search pauses the
  // group, the held pill back when the search is cleared. A pill the
  // operator picks during the search has already been reported by
  // handleChange, which also ends the pause — nothing to repeat.
  const wasPausedRef = useRef(isPaused)
  useEffect(() => {
    if (wasPausedRef.current === isPaused) return
    wasPausedRef.current = isPaused
    const effective =
      isPaused || value === null ? null : resolveOption(value).key
    if (lastReportedRef.current === effective) return
    lastReportedRef.current = effective
    onChange?.(isPaused ? null : value, effective)
  }, [isPaused, value, onChange, resolveOption])

  const handleChange = useCallback(
    (newValue: number | null, filterValue?: string, filterColumn?: string) => {
      setValue(newValue)
      // Resolve the canonical filter string (the registered
      // filterValue if the option declared one, otherwise the
      // stringified numeric value). This is what callers want when
      // they bucket on a typed string union.
      const filterString =
        newValue === null ? null : (filterValue ?? String(newValue))
      onChange?.(newValue, filterString)
      lastReportedRef.current = filterString

      if (newValue === null) {
        clearStatusFilter()
      } else {
        setStatusFilter(filterValue ?? String(newValue), filterColumn)
      }

      // A pill picked while the search has the pills paused narrows the
      // search by it: the pills resume until the search is cleared.
      if (isStatusFilterPaused) resumeStatusFilter()

      if (urlParam) {
        // A `value={null}` option with a `filterValue` keeps that string as
        // its URL key, so the pill survives a reload and can be deep-linked.
        const nextUrl = filterString ?? filterValue ?? null
        // Avoid emitting the same URL twice in a row -- React Router
        // will still re-render but our other params would get rewritten
        // as a side-effect.
        if (lastUrlSyncRef.current === nextUrl) return
        lastUrlSyncRef.current = nextUrl

        setSearchParams({ [urlParam]: nextUrl }, { replace: true })
      }
    },
    [
      clearStatusFilter,
      setStatusFilter,
      onChange,
      isStatusFilterPaused,
      resumeStatusFilter,
      urlParam,
      setSearchParams,
    ],
  )

  const ctxValue = useMemo<StatusFilterContextValue>(
    () => ({
      value,
      isPaused,
      onChange: handleChange,
      registerOption,
      isLoading,
    }),
    [value, isPaused, handleChange, registerOption, isLoading],
  )

  return (
    <StatusFilterContext.Provider value={ctxValue}>
      <div
        className={cn(
          "ui-table-status-filter",
          isPaused && "ui-table-status-filter-is-paused",
          className,
        )}
        {...divProps}
      >
        {children}
        {/* The reason the pills just dimmed, where the eye is: a search
            covers every row, and the held pill (dashed) is what comes back. */}
        {isPaused && (
          <Tooltip>
            <Tooltip.Trigger asChild>
              <span className="ui-table-status-filter-paused-note">
                <Icon src="pause" size={12} />
                Paused while searching
              </span>
            </Tooltip.Trigger>
            <Tooltip.Content
              side="bottom"
              className="ui-tooltip-content--panel max-w-[280px]"
            >
              <span className="block px-3 py-2">
                The search covers every row, whichever tab is selected. Clear
                the search to filter by the tab again, or pick a tab to narrow
                the search.
              </span>
            </Tooltip.Content>
          </Tooltip>
        )}
      </div>
    </StatusFilterContext.Provider>
  )
}

export type TableStatusFilterOptionProps = Omit<
  ComponentProps<"button">,
  "value"
> & {
  value: number | null
  /**
   * Value sent to the server filter. Defaults to `String(value)` when
   * omitted. On the `value={null}` ("All") option nothing is filtered, but
   * the string is still the key a URL resolves the pill by: give it one
   * (`filterValue="all"`) and `?<urlParam>=all` deep-links to All.
   */
  filterValue?: string
  /**
   * TanStack column ID to filter when this option is selected, overriding
   * the group's `column`. Lets a single tab group mix mutually-exclusive
   * options that target different columns (e.g. a "Flagged" tab beside the
   * status tabs that filters `IsFlagged` instead of `UserStatusCode`).
   */
  filterColumn?: string
  count?: number
  variant?: "success" | "destructive" | "warning"
  iconClassName?: IconProps["className"]
  iconSrc?: IconProps["src"]
  iconSize?: IconProps["size"]
  iconStyle?: IconProps["style"]
}

function TableStatusFilterOption(props: TableStatusFilterOptionProps) {
  const {
    value,
    filterValue,
    filterColumn,
    count,
    className,
    children,
    variant,
    iconSrc,
    iconSize = 14,
    iconClassName,
    iconStyle,
    ...buttonProps
  } = props

  const {
    value: selectedValue,
    isPaused,
    onChange,
    registerOption,
    isLoading,
  } = useStatusFilterContext()
  const isSelected = selectedValue === value
  // While the search pauses the group, the selected pill is held rather
  // than applied: it loses the active fill and keeps a dashed outline.
  const isActive = isSelected && !isPaused
  const isHeld = isSelected && isPaused

  // Register so the parent can resolve URL strings → numeric value.
  // Cheap, idempotent, and runs in a layout effect so the parent's
  // initial sync (queued via microtask) sees us.
  useEffect(() => {
    registerOption(value, filterValue, filterColumn)
  }, [registerOption, value, filterValue, filterColumn])

  return (
    <button
      className={cn(
        "ui-table-status-filter-tab",
        isActive && "ui-table-status-filter-tab-active",
        isHeld && "ui-table-status-filter-tab-is-held",
        className,
      )}
      onClick={() => {
        onChange(value, filterValue, filterColumn)
      }}
      {...buttonProps}
    >
      {(variant || iconSrc) && (
        <Icon
          className={cn("ui-table-status-filter-tab-icon", iconClassName, {
            "ui-table-status-filter-tab-icon-success": variant === "success",
            "ui-table-status-filter-tab-icon-destructive":
              variant === "destructive",
            "ui-table-status-filter-tab-icon-warning": variant === "warning",
          })}
          src={
            iconSrc ||
            (variant === "success"
              ? "check-circle"
              : variant === "warning"
                ? "exclamation"
                : "trash")
          }
          size={iconSize}
          style={iconStyle}
          loading={isLoading}
        />
      )}
      {children}
      {count !== undefined && !isLoading && (
        <span className="ui-table-status-filter-count">{count}</span>
      )}
    </button>
  )
}

export default Object.assign(TableStatusFilter, {
  Option: TableStatusFilterOption,
})
